import { IssueStatus } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Customer-facing labels for the CRM issue workflow (PD-13773) — the
 * internal "To-Do" reads as "Open" to a customer, and "Done" as
 * "Resolved". Unknown / custom statuses fall through unmapped.
 */
function statusLabel(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.TODO:
			return 'Open';
		case IssueStatus.IN_PROGRESS:
			return 'In Progress';
		case IssueStatus.DONE:
			return 'Resolved';
		default:
			return status;
	}
}

/** Pill colors matching the storefront's transaction-status treatment. */
function statusClasses(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.TODO:
			return 'bg-accent-badge text-accent-tint-fg border border-accent-tint';
		case IssueStatus.IN_PROGRESS:
			return 'bg-warning-badge text-warning-tint-fg border border-warning-tint';
		case IssueStatus.DONE:
			return 'bg-success-badge text-success-tint-fg border border-success-tint';
		default:
			return 'bg-sunken text-strong';
	}
}

/**
 * Status pill for a support request (PD-13773) — shared by the Support
 * list and the issue-thread page so the two always agree.
 */
export function IssueStatusChip({ status }: { status: string }): JSX.Element {
	const { t } = useTranslation();

	return (
		<span
			className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusClasses(status)}`}
		>
			{t(statusLabel(status))}
		</span>
	);
}
